import { IoException } from '@core/Exceptions'
import SparkMD5 from 'spark-md5'
import type { HttpRequestCancelToken } from './HttpRequestCancelToken'
import type { Fn } from '@vueuse/core'

const UPLOAD_FILE_CHUNK_SIZE = 10 * 1024 * 1024

export const UPLOAD_MAX_SIZE_SINGLE_FILE_IN_BYTES = 20 * 1024 * 1024

export const DOWNLOAD_FILE_CHUNK_SIZE = 10 * 1024 * 1024

export const LAN_DOWNLOAD_FILE_CHUNK_SIZE = 30 * 1024 * 1024

export const WAN_DOWNLOAD_MAX_SIZE_SINGLE_FILE_IN_BYTES = 50 * 1024 * 1024

export const LAN_DOWNLOAD_MAX_SIZE_SINGLE_FILE_IN_BYTES = 200 * 1024 * 1024 // 初步测试，在edge浏览器版本 105.0.1343.42 (正式版本) (64 位)上面，崩溃阈值为500MB左右，谨慎起见，设置阈值为200MB

export const LAN_DOWNLOAD_MAX_SIZE_SINGLE_FILE_IN_BYTES_RANGE = 500 * 1024 * 1024 // 分片下载可以适当将局域网流下载阈值提高

interface FilePart {
  /**
   * 当前分片大小
   */
  readonly size: number
  /**
   * 文件Blob对象
   */
  readonly chunk: Blob
  /**
   * 分片序列号
   */
  readonly index: number

  readonly name: string
}

export class FileMultiparts {
  #index = 0
  #fileChunks: FilePart[] = []
  readonly totalSize: number
  readonly totalChunks: number
  readonly sizePerChunk: number

  constructor(
    file: File,
    sizePerChunk: number = UPLOAD_FILE_CHUNK_SIZE,
    from = 0,
    restFileParts?: number,
    restFilePartsIndex?: number,
    requestCancelToken?: HttpRequestCancelToken
  ) {
    this.totalSize = file.size
    this.sizePerChunk = sizePerChunk
    this.#index = restFilePartsIndex || 0

    for (let cur = from; cur < this.totalSize; cur += this.sizePerChunk) {
      if (requestCancelToken?.isCancelled) break

      const chunk = Blob.prototype.slice.call(file, cur, cur + this.sizePerChunk)

      this.#fileChunks.push({
        index: this.#index++,
        chunk,
        size: chunk.size,
        name: file.name,
      })
    }
    this.totalChunks = restFileParts || this.#fileChunks.length
  }

  *[Symbol.iterator]() {
    while (this.#fileChunks.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      yield this.#fileChunks.shift()!
    }
  }
}

/**
 * 计算文件的md5值
 *
 * @param file 需要计算的文件对象
 * @param requestCancelToken 取消请求句柄
 * @returns  MD5 string
 */

export async function getFileMd5(
  file: File,
  requestCancelToken?: HttpRequestCancelToken,
  readCallback?: Fn
): Promise<string> {
  const fileMultiparts = new FileMultiparts(file)

  const spark = new SparkMD5.ArrayBuffer()
  const fileReader = new FileReader()

  function readFile(chunk: Blob): Promise<void> {
    return new Promise((resolve) => {
      fileReader.onload = function () {
        if (fileReader.result !== null && typeof fileReader.result !== 'string') {
          spark.append(fileReader.result)
          resolve()
        }
      }

      fileReader.onerror = (err) => {
        throw new IoException(`read file failed${JSON.stringify(err)}`)
      }

      fileReader.readAsArrayBuffer(chunk)
    })
  }

  for (const filePart of fileMultiparts) {
    if (requestCancelToken?.isCancelled) break

    if (filePart) {
      await readFile(filePart.chunk)
      readCallback?.()
    }
  }

  return spark.end()
}
